import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import Attachments from "../components/Attachments";
import { toast } from "sonner";
import {
    ClipboardList,
    Truck,
    Boxes,
    Scale,
    PackageCheck,
    ServerCog,
    Check,
    X,
    Undo2,
    Printer,
    Send,
} from "lucide-react";

const money = (v) =>
    Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const num = (v, d = 2) =>
    Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
    });

const TREASURY_MODES = ["bank transfer", "cash deposit", "check deposit", "cheque deposit", "bank_transfer", "cash_deposit", "check_deposit", "peso net", "peso_net", "pesonet", "cash", "check", "bank"];
const SO_ATTACH_KINDS = [
    ["card_deposit_slip", "Card / Deposit slip"],
    ["bdo_qr_ref", "BDO QR PH ref"],
    ["gcash_ref", "G-Cash ref"],
    ["sales_quotation", "Sales Quotation"],
    ["mother_po", "Mother PO"],
    ["sales_agreement", "Sales Agreement"],
    ["sales_invoice", "Sales Invoice (SI)"],
    ["collection_receipt", "Collection Receipt (CR)"],
    ["acknowledgement_receipt", "Acknowledgement Receipt (AR)"],
    ["collection_date", "Collection Date"],
    ["delivery_timeline", "Delivery Timeline"],
    ["other", "Other"],
];
// UAT 10-26: SO attachments are split into a PAYMENT section and a DOCUMENT section. Card and Deposit
// slip are separate kinds; the specific SAP documents are no longer offered for manual upload, but any
// already uploaded still display (via displayKinds + onlyKinds) so nothing is hidden.
const SO_PAYMENT_UPLOAD = [
    ["card", "Card"],
    ["deposit_slip", "Deposit slip"],
    ["bdo_qr_ref", "BDO QR PH ref"],
    ["gcash_ref", "G-Cash ref"],
];
const SO_PAYMENT_DISPLAY = [...SO_PAYMENT_UPLOAD, ["card_deposit_slip", "Card / Deposit slip (legacy)"]];
const SO_DOC_UPLOAD = [
    ["collection_date", "Collection Date"],
    ["other", "Other"],
];
const SO_DOC_DISPLAY = [
    ...SO_DOC_UPLOAD,
    ["delivery_timeline", "Delivery Timeline"],
    ["sales_quotation", "Sales Quotation"],
    ["mother_po", "Mother PO"],
    ["sales_agreement", "Sales Agreement"],
    ["sales_invoice", "Sales Invoice (SI)"],
    ["collection_receipt", "Collection Receipt (CR)"],
    ["acknowledgement_receipt", "Acknowledgement Receipt (AR)"],
];
const SO_PAYMENT_ONLY = SO_PAYMENT_DISPLAY.map((k) => k[0]);
const SO_DOC_ONLY = SO_DOC_DISPLAY.map((k) => k[0]);
function soPaymentVerified(so) {
    return !!(so && so.treasury_verified_at)
        || String((so && so.payment_verification) || "").toLowerCase().startsWith("verified")
        || ["approved", "posted_to_sap", "partially_delivered", "delivered", "invoiced", "completed", "closed"].includes(so && so.status);
}
function paymentNeedsTreasury(pt) {
    return TREASURY_MODES.includes(String(pt || "").trim().toLowerCase());
}

function lineOrdered(l) {
    return Number(l.ordered_quantity ?? l.quantity ?? 0);
}

function lineDelivered(l) {
    return Number(l.delivered_quantity ?? 0);
}

function lineRemaining(l) {
    const ordered = lineOrdered(l);
    const delivered = lineDelivered(l);
    return Number(l.remaining_quantity ?? Math.max(0, ordered - delivered));
}

function totals(so) {
    const lines = so?.lines || [];
    return {
        ordered: lines.reduce((a, l) => a + lineOrdered(l), 0),
        delivered: lines.reduce((a, l) => a + lineDelivered(l), 0),
        remaining: lines.reduce((a, l) => a + lineRemaining(l), 0),
        weight: lines.reduce((a, l) => a + Number(l.total_weight || 0), 0),
        boxes: lines.reduce((a, l) => a + Number(l.estimated_boxes || 0), 0),
        advancedPO: lines.filter((l) =>
            l.advanced_po_allowed ||
            l.availability_status === "not_enough" ||
            l.availability_status === "without_stock" ||
            l.allocation?.status === "not_enough" ||
            l.allocation?.status === "without_stock"
        ).length,
    };
}

export default function SalesOrders({ depositMode = false }) {
    const { user, hasPermission } = useAuth();
    const showPrices = user?.price_visibility !== "none";  // UAT 10-28: hidden-price group (none) sees no prices here either
    const [rows, setRows] = useState([]);
    const [soView, setSoView] = useState("final");   // final | drafts | all
    const [dealer, setDealer] = useState("");        // per-dealer (customer) monitoring filter
    const FINAL_ST = ["approved", "posted_to_sap", "partially_delivered", "delivered", "invoiced", "completed", "closed"];
    const isFinalSO = (s) => FINAL_ST.includes(s?.status);
    // Deposit Slips console (Sir Lynx 2026-10-29): filter by customer type. Mutually exclusive, in
    // this order: Walk-in (the generic WALKIN / WALK-IN/CASH customer), then Non-Organic, else Organic.
    const [custType, setCustType] = useState("");   // "" | organic | non_organic | walkin
    const orderType = (s) => (String(s.customer_code || "").toUpperCase() === "WALKIN" || /^walk-?in/i.test(s.customer_name || ""))
        ? "walkin" : ((s.business_mode === "non_organic" || s.order_source === "non_organic") ? "non_organic" : "organic");
    const typeRows = custType ? rows.filter((r) => orderType(r) === custType) : rows;
    const dealers = Array.from(new Set(typeRows.map((r) => r.customer_name).filter(Boolean))).sort();
    const _baseRows = soView === "all" ? typeRows : typeRows.filter(soView === "final" ? isFinalSO : (s) => !isFinalSO(s));
    const visibleRows = dealer ? _baseRows.filter((s) => (s.customer_name || "") === dealer) : _baseRows;
    const [approvals, setApprovals] = useState([]);
    const [selected, setSelected] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [comment, setComment] = useState("");

    const load = async () => {
        const [soRes, apprRes] = await Promise.all([
            api.get(depositMode ? "/sales-orders?with_deposit=true" : "/sales-orders"),
            api.get("/approvals").catch(() => ({ data: [] })),
        ]);
        setRows(soRes.data);
        setApprovals(apprRes.data || []);
    };

    useEffect(() => {
        load();
    }, []);

    const summary = useMemo(() => {
        return {
            orders: rows.length,
            ordered: rows.reduce((a, s) => a + totals(s).ordered, 0),
            delivered: rows.reduce((a, s) => a + totals(s).delivered, 0),
            remaining: rows.reduce((a, s) => a + totals(s).remaining, 0),
            weight: rows.reduce((a, s) => a + totals(s).weight, 0),
            sapOrders: rows.filter((s) => s.sap_status === "posted_to_sap" || s.sap_docentry).length,
        };
    }, [rows]);

    const reloadSelected = async (id) => {
        try {
            const { data } = await api.get(`/sales-orders/${id}`);
            setSelected(data);
        } catch (_) {
            setSelected(null);
        }
    };

    const pendingApprovalFor = (so) => {
        if (!so) return null;
        return approvals.find((a) =>
            a.document_type === "sales_order" &&
            a.document_id === so.id &&
            a.status === "pending"
        );
    };

    const approvalAction = async (so, action) => {
        const appr = pendingApprovalFor(so);
        if (!appr) {
            toast.error("No pending approval request found for this Sales Order.");
            return;
        }

        setBusyId(so.id);
        try {
            await api.post(`/approvals/${appr.id}/act`, { action, comment });
            toast.success(`${action} done`);
            setComment("");
            await load();
            await reloadSelected(so.id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const recordPayment = async (so, { amount, reference }) => {
        setBusyId(so.id);
        try {
            await api.post(`/sales-orders/${so.id}/record-payment`, { amount, reference });
            toast.success("Payment recorded \u2014 awaiting TD verification.");
            await load();
            await reloadSelected(so.id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const closeSO = async (so) => {
        setBusyId(so.id);
        try {
            await api.post(`/sales-orders/${so.id}/close`, {});
            toast.success("Sales Order closed.");
            await load();
            await reloadSelected(so.id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const verifyPayment = async (so, status, amount) => {
        setBusyId(so.id);
        try {
            await api.post(`/sales-orders/${so.id}/verify-payment`, { status, amount });
            toast.success(status === "cleared" ? "Payment marked Cleared (Paid)." : "Payment marked For Clearing.");
            await load();
            await reloadSelected(so.id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const postToSap = async (so) => {
        setBusyId(so.id);
        try {
            const { data } = await api.post(`/sales-orders/${so.id}/post-to-sap`);
            toast.success(`SAP Sales Order posted: ${data.sap_docnum || data.sap_docentry}`);
            await load();
            await reloadSelected(so.id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const createPicklist = async (so) => {
        setBusyId(so.id);
        try {
            await api.post("/picklists", {
                sales_order_id: so.id,
                warehouse_code: so.lines?.[0]?.warehouse_code || "WH1",
            });
            toast.success("Picklist created");
            await load();
            await reloadSelected(so.id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const markInTransit = async (so) => {
        setBusyId(so.id);
        try {
            await api.post(`/sales-orders/${so.id}/mark-in-transit`);
            toast.success("Inventory marked in transit");
            await load();
            await reloadSelected(so.id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const receiveInventory = async (so) => {
        setBusyId(so.id);
        try {
            await api.post(`/sales-orders/${so.id}/receive-inventory`);
            toast.success("Inventory received");
            await load();
            await reloadSelected(so.id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const submitForApproval = async (so) => {
        setBusyId(so.id);
        try {
            await api.post(`/sales-orders/${so.id}/submit`);
            toast.success("Sales Order submitted for approval");
            await load();
            await reloadSelected(so.id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const openModal = async (so) => {
        setSelected(so);
        await reloadSelected(so.id);
    };

    return (
        <div className="p-3 md:p-6 space-y-5 md:space-y-6" data-testid="sales-orders-page">
            <div>
                <div className="sc-section-title">Fulfillment</div>
                <h1 className="text-xl md:text-2xl font-semibold tracking-tight">{depositMode ? "Deposit Slips" : "Sales Orders"}</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    {depositMode
                        ? "Deposit slips attached to Sales Orders — review and clear with the verified amount."
                        : "Click a Sales Order to review details and perform available actions."}
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <SummaryCard icon={ClipboardList} label="Sales Orders" value={num(summary.orders, 0)} />
                <SummaryCard icon={PackageCheck} label="Ordered Qty" value={num(summary.ordered, 0)} />
                <SummaryCard icon={Truck} label="Delivered Qty" value={num(summary.delivered, 0)} />
                <SummaryCard icon={Boxes} label="Remaining Qty" value={num(summary.remaining, 0)} />
                <SummaryCard icon={ServerCog} label="SAP Orders" value={num(summary.sapOrders, 0)} />
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm">
                <div className="flex flex-wrap gap-1">
                    {[["final", "Sales Orders"], ["drafts", "Drafts / For Revision"], ["all", "All"]].map(([k, l]) => (
                        <button key={k} type="button" onClick={() => setSoView(k)}
                            className={`px-3 py-1.5 rounded-md border ${soView === k ? "bg-primary text-primary-foreground border-primary" : "bg-transparent text-muted-foreground"}`}>
                            {l}{k === "drafts" ? ` (${rows.filter((s) => !isFinalSO(s)).length})` : ""}
                        </button>
                    ))}
                </div>
                {depositMode && (
                    <select value={custType} onChange={(e) => { setCustType(e.target.value); setDealer(""); }} className="sc-input h-8 text-sm max-w-[200px] ml-auto" data-testid="customer-type-filter" title="Filter by customer type">
                        <option value="">All customer types ({rows.length})</option>
                        <option value="organic">Organic ({rows.filter((r) => orderType(r) === "organic").length})</option>
                        <option value="non_organic">Non-Organic ({rows.filter((r) => orderType(r) === "non_organic").length})</option>
                        <option value="walkin">Walk-in Customer ({rows.filter((r) => orderType(r) === "walkin").length})</option>
                    </select>
                )}
                <select value={dealer} onChange={(e) => setDealer(e.target.value)} className={"sc-input h-8 text-sm max-w-[260px] " + (depositMode ? "" : "ml-auto")} data-testid="dealer-filter" title="Filter by dealer (customer)">
                    <option value="">All dealers ({dealers.length})</option>
                    {dealers.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1350px]">
                        <thead>
                            <tr>
                                <th>SO</th>
                                <th>Customer</th>
                                <th>Source Quotation</th>
                                <th>PO No.</th>
                                <th>Status</th>
                                <th className="text-right">Amount</th>
                                <th>Deposit Slip</th>
                                <th>TD Confirmation</th>
                                <th>SAP Order</th>
                                <th className="text-right">Ordered</th>
                                <th className="text-right">Delivered</th>
                                <th className="text-right">Remaining</th>
                                <th className="text-right">Boxes</th>
                                <th className="text-right">Weight</th>
                                <th>Advanced PO</th>
                            </tr>
                        </thead>

                        <tbody>
                            {visibleRows.length === 0 && (
                                <tr>
                                    <td colSpan={15} className="text-center py-8 text-muted-foreground">
                                        No sales orders yet. Convert an approved quotation.
                                    </td>
                                </tr>
                            )}

                            {visibleRows.map((s) => {
                                const t = totals(s);
                                const hasSapOrder = !!s.sap_docentry || s.sap_status === "posted_to_sap";
                                return (
                                    <tr
                                        key={s.id}
                                        onClick={() => openModal(s)}
                                        className="cursor-pointer hover:bg-secondary/50"
                                        data-testid={`so-row-${s.id}`}
                                    >
                                        <td>
                                            <div className="font-mono text-xs underline underline-offset-2">{s.doc_no}</div>
                                            <div className="text-[11px] text-muted-foreground">
                                                {new Date(s.created_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}
                                            </div>
                                        </td>

                                        <td>{s.customer_name}</td>

                                        <td className="font-mono text-xs text-muted-foreground">
                                            {s.source_quotation_no || "—"}
                                        </td>

                                        <td className="font-mono text-xs">{s.po_no || "—"}</td>

                                        <td>
                                            <StatusBadge status={s.status} />
                                            {s.pp_status && (
                                                <div className="mt-1">
                                                    <span className={`sc-pill ${s.pp_status === "picked" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                                        {s.pp_status === "picked" ? "Picked" : "Partially picked"}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="text-[11px] text-muted-foreground mt-1">
                                                SAP: {s.sap_status || "not_posted"}
                                            </div>
                                        </td>

                                        <td className="sc-num font-semibold text-right">{showPrices ? `₱${money(s.grand_total ?? s.subtotal)}` : "—"}</td>

                                        <td className="text-xs">
                                            {(s.deposit_slip_attached || s.card_deposit_slip)
                                                ? <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-medium">Attached</span>
                                                : <span className="text-muted-foreground">—</span>}
                                        </td>

                                        <td className="text-xs">
                                            {(() => {
                                                const ps = s.payment_status;
                                                const lbl = (ps === "cleared" || ps === "paid") ? "Cleared (Paid)"
                                                    : (ps === "for_clearing" || ps === "awaiting_treasury") ? (s.treasury_verified_by ? "For Clearing" : "For Treasury Verification")
                                                    : ps === "not_posted" ? "Not Posted" : "—";
                                                return (
                                                    <div>
                                                        <div>{lbl}</div>
                                                        {showPrices && s.verified_amount ? <div className="text-[10px] text-muted-foreground">₱{money(s.verified_amount)}</div> : null}
                                                        {s.treasury_verified_by ? <div className="text-[10px] text-muted-foreground truncate max-w-[150px]" title={s.payment_verification || ""}>by {s.treasury_verified_by}</div> : null}
                                                    </div>
                                                );
                                            })()}
                                        </td>

                                        <td>
                                            {hasSapOrder ? (
                                                <div>
                                                    <StatusBadge status="posted_to_sap" />
                                                    <div className="text-[11px] text-muted-foreground mt-1">
                                                        DB: <span className="font-mono">{s.sap_company_db || "—"}</span>
                                                    </div>
                                                    <div className="text-[11px] text-muted-foreground">
                                                        Order: <span className="font-mono">{s.sap_docnum || s.sap_docentry}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <StatusBadge status={s.sap_status || "not_posted"} />
                                            )}

                                            {s.sap_error && (
                                                <div className="text-[11px] text-red-600 mt-1 max-w-xs truncate" title={s.sap_error}>
                                                    {s.sap_error}
                                                </div>
                                            )}
                                        </td>

                                        <td className="sc-num">{num(t.ordered, 0)}</td>
                                        <td className="sc-num">{num(t.delivered, 0)}</td>
                                        <td className="sc-num font-semibold">{num(t.remaining, 0)}</td>
                                        <td className="sc-num">{num(t.boxes, 2)}</td>
                                        <td className="sc-num">{num(t.weight, 2)} kg</td>

                                        <td className="text-xs text-muted-foreground">
                                            {t.advancedPO > 0 ? `${t.advancedPO} line(s) pending Purchasing review` : "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {selected && (
                <SalesOrderModal
                    so={selected}
                    user={user}
                    hasPermission={hasPermission}
                    busy={busyId === selected.id}
                    comment={comment}
                    setComment={setComment}
                    onClose={() => setSelected(null)}
                    onApprove={() => approvalAction(selected, "approve")}
                    onRevise={() => approvalAction(selected, "revise")}
                    onReject={() => approvalAction(selected, "reject")}
                    onPostToSap={() => postToSap(selected)}
                    onCreatePicklist={() => createPicklist(selected)}
                    onMarkInTransit={() => markInTransit(selected)}
                    onReceiveInventory={() => receiveInventory(selected)}
                    onSubmit={() => submitForApproval(selected)}
                    onVerifyPayment={(status, amount) => verifyPayment(selected, status, amount)}
                    onRecordPayment={(opts) => recordPayment(selected, opts)}
                    onCloseSO={() => closeSO(selected)}
                    pendingApproval={pendingApprovalFor(selected)}
                    onUpdated={(u) => setSelected(u)}
                />
            )}
        </div>
    );
}

// SA-3: CSR dropdown option lists (exact values from Jenna's Sales Agent Module sheet).
const CSR_OPTS = {
    lead_source: ["Walk-in New", "Walk-in Old", "Project New", "Project Old", "Store/Showroom", "Website"],
    category: ["Referral", "End User", "Architect", "Engineer", "IDR", "Others"],
    buyer: ["Owner", "Contractor", "Developer", "End User", "Specifier", "Architect", "Engineer", "IDR", "Purchaser", "Others"],
    classification: ["Commercial", "Residential"],
    source_platform: ["Passerby", "Facebook (FB)", "Instagram (IG)", "Website", "TikTok", "Inbound Call", "Outbound Call", "SMS (Text-Based Inquiry)", "Landline Call", "Referral", "Tarpaulin", "Catalogues Distributed", "Special Assignment by COO or CEO", "Saturation", "With Specifier", "Gmail Sales", "In-House"],
    sprf: ["Yes", "No"],
};

// Module-level so inputs keep focus across parent re-renders.
function CsrField({ label, k, form, set, editable, options }) {
    if (!editable) {
        return (
            <div>
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="font-medium mt-1">{form[k] || "—"}</div>
            </div>
        );
    }
    return (
        <div>
            <label className="text-xs text-muted-foreground">{label}</label>
            {options ? (
                <select className="sc-input mt-1" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`so-csr-${k}`}>
                    <option value="">—</option>
                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            ) : (
                <input className="sc-input mt-1" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`so-csr-${k}`} />
            )}
        </div>
    );
}

// SA-3: CSR capture card — editable dropdown fields + Mode of Payment while the SO is
// still draft / for revision / rejected. Saves via PUT /sales-orders/{id}.
function CsrCard({ so, editable, onUpdated }) {
    const [modes, setModes] = useState([]);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({});

    useEffect(() => {
        api.get("/payment-types")
            .then((r) => setModes((r.data || []).slice().sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99))))
            .catch(() => {});
    }, []);

    useEffect(() => {
        setForm({
            lead_source: so.lead_source || "",
            category: so.category || "",
            buyer: so.buyer || "",
            classification: so.classification || "",
            source_platform: so.source_platform || "",
            bmf_ref: so.bmf_ref || "",
            sprf: so.sprf || "",
            sprf_pct: so.sprf_pct || "",
            advance_sale: !!so.advance_sale,
            beneficiary_name: so.beneficiary_name || "",
            payment_type: so.payment_type || "",
            sap_card_code: so.sap_card_code || "",
            po_no: so.po_no || "",
            po_by_group: so.po_by_group || {},
            notes: so.notes || "",
            for_posting: so.for_posting === true ? "yes" : so.for_posting === false ? "no" : "",
        });
    }, [so.id, so.updated_at]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const is888 = /888/.test(`${so.company || ""} ${so.schema_type || ""} ${(so.lines && so.lines[0] && so.lines[0].schema_name) || ""}`);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form };
            if (payload.for_posting === "yes") payload.for_posting = true;
            else if (payload.for_posting === "no") payload.for_posting = false;
            else delete payload.for_posting;
            const { data } = await api.put(`/sales-orders/${so.id}`, payload);
            toast.success("Sales order updated");
            if (onUpdated) onUpdated(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="sc-card p-4 space-y-3" data-testid="so-csr-card">
            <div className="sc-section-title">CSR Details</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
{editable ? (
                    <label className="flex items-center gap-2 text-sm md:col-span-3"><input type="checkbox" checked={!!form.advance_sale} onChange={(e) => set("advance_sale", e.target.checked)} data-testid="so-csr-advance_sale" /> Advance sale (customer paid, no PO yet)</label>
                ) : (form.advance_sale ? (<div><div className="text-xs text-muted-foreground">Advance sale</div><div className="font-medium mt-1">Yes (paid, no PO yet)</div></div>) : null)}
                <CsrField label="Lead Source" k="lead_source" form={form} set={set} editable={editable} options={CSR_OPTS.lead_source} />
                <CsrField label="Category" k="category" form={form} set={set} editable={editable} options={CSR_OPTS.category} />
                <CsrField label="Buyer" k="buyer" form={form} set={set} editable={editable} options={CSR_OPTS.buyer} />
                <CsrField label="Classification" k="classification" form={form} set={set} editable={editable} options={CSR_OPTS.classification} />
                <CsrField label="Source Platform" k="source_platform" form={form} set={set} editable={editable} options={CSR_OPTS.source_platform} />
                <CsrField label="SPRF" k="sprf" form={form} set={set} editable={editable} options={CSR_OPTS.sprf} />
                {form.sprf === "Yes" && (
                    <CsrField label="Beneficiary Name" k="beneficiary_name" form={form} set={set} editable={editable} />
                )}
                {form.sprf === "Yes" && (editable ? (
                    <div>
                        <label className="text-xs text-muted-foreground">SPRF %</label>
                        <input type="number" min="0" max="100" step="0.01" className="sc-input mt-1" value={form.sprf_pct || ""} onChange={(e) => set("sprf_pct", e.target.value)} data-testid="so-csr-sprf_pct" />
                    </div>
                ) : (
                    <div><div className="text-xs text-muted-foreground">SPRF %</div><div className="font-medium mt-1">{form.sprf_pct ? `${form.sprf_pct}%` : "—"}</div></div>
                ))}
                <CsrField label="BMF REF#" k="bmf_ref" form={form} set={set} editable={editable} />
                <CsrField label="SAP Customer Code (optional)" k="sap_card_code" form={form} set={set} editable={editable} />
                <div><div className="text-xs text-muted-foreground">PO No. (auto)</div><div className="font-medium mt-1 font-mono">{so.po_no || so.doc_no || "—"}</div></div>
                {(() => {
                    const groups = Array.from(new Map((so.lines || []).map((l) => {
                        const key = String(l.item_group ?? l.item_group_name ?? "DEFAULT");
                        return [key, l.item_group_name || key];
                    })).entries());
                    if (groups.length < 2) return null;
                    const pg = form.po_by_group || {};
                    const setG = (k, v) => setForm((f) => ({ ...f, po_by_group: { ...(f.po_by_group || {}), [k]: v } }));
                    return (
                        <div className="md:col-span-3 border border-dashed border-border rounded-md p-3" data-testid="so-po-by-group">
                            <div className="text-xs font-medium mb-1">Separate PO per item group <span className="text-muted-foreground font-normal">(each group posts its own SAP Sales Order; leave blank to keep a single PO)</span></div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {groups.map(([k, name]) => (
                                    <div key={k}>
                                        <label className="text-[11px] text-muted-foreground">{name}</label>
                                        {editable
                                            ? <input className="sc-input mt-0.5 h-8 text-sm" value={pg[k] || ""} onChange={(e) => setG(k, e.target.value)} placeholder={`PO for ${name}`} data-testid={`so-po-group-${k}`} />
                                            : <div className="mt-0.5 text-sm font-mono">{pg[k] || "—"}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })()}
                <div className="md:col-span-3">
                    <label className="text-xs text-muted-foreground">Remarks</label>
                    {editable ? (
                        <textarea className="sc-input mt-1 w-full" rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Remarks" data-testid="so-remarks" />
                    ) : (
                        <div className="mt-1 text-sm whitespace-pre-wrap">{form.notes || "—"}</div>
                    )}
                </div>
                <div>
                    {editable ? (
                        <>
                            <label className="text-xs text-muted-foreground">Mode of Payment</label>
                            <select className="sc-input mt-1" value={form.payment_type || ""} onChange={(e) => set("payment_type", e.target.value)} data-testid="so-payment-mode">
                                <option value="">—</option>
                                {modes.map((m) => <option key={m.code} value={m.name}>{m.name}</option>)}
                            </select>
                            {form.payment_type && (
                                <div className={`text-[11px] mt-1 font-medium ${paymentNeedsTreasury(form.payment_type) ? "text-amber-700" : "text-muted-foreground"}`} data-testid="so-payment-route">
                                    {paymentNeedsTreasury(form.payment_type) ? "For verification to Treasury" : "Direct to Accounting AR"}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="text-xs text-muted-foreground">Mode of Payment</div>
                            <div className="font-medium mt-1">{form.payment_type || "—"}</div>
                        </>
                    )}
                </div>
                {is888 && (
                    <div>
                        {editable ? (
                            <>
                                <label className="text-xs text-muted-foreground">For Posting (888)</label>
                                <select className="sc-input mt-1" value={form.for_posting || ""} onChange={(e) => set("for_posting", e.target.value)} data-testid="so-for-posting">
                                    <option value="">Not decided</option>
                                    <option value="yes">For Posting</option>
                                    <option value="no">Not For Posting</option>
                                </select>
                            </>
                        ) : (
                            <>
                                <div className="text-xs text-muted-foreground">For Posting (888)</div>
                                <div className="font-medium mt-1">{so.for_posting === true ? "For Posting" : so.for_posting === false ? "Not For Posting" : "—"}</div>
                            </>
                        )}
                    </div>
                )}
            </div>
            {editable && (
                <div className="flex justify-end">
                    <button onClick={save} disabled={saving} className="h-9 px-4 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50" data-testid="so-csr-save">
                        Save CSR details
                    </button>
                </div>
            )}
        </div>
    );
}

function SalesOrderModal({
    so,
    user,
    hasPermission,
    busy,
    comment,
    setComment,
    onClose,
    onApprove,
    onRevise,
    onReject,
    onPostToSap,
    onCreatePicklist,
    onMarkInTransit,
    onReceiveInventory,
    onSubmit,
    onVerifyPayment,
    onRecordPayment,
    onCloseSO,
    pendingApproval,
    onUpdated,
}) {
    const t = totals(so);
    const showPrices = user?.price_visibility !== "none";  // UAT 10-28: hide prices from the hidden-price group (none)
    const [verifyAmt, setVerifyAmt] = useState(so.grand_total ?? so.subtotal ?? "");
    const [payRef, setPayRef] = useState("");
    const hasSapOrder = !!so.sap_docentry || so.sap_status === "posted_to_sap";
    const canApprove = !!pendingApproval && (user?.role === "admin" || user?.role === pendingApproval.approver_role);
    const canPostSap = hasPermission("sales_order.write") && so.status === "approved" && !hasSapOrder;
    const canPicklist = hasPermission("picklist.write") && so.status === "approved" && hasSapOrder && so.transaction_type !== "inventory_replenishment";
    const canMarkInTransit =
        hasPermission("sales_order.write") &&
        so.transaction_type === "inventory_replenishment" &&
        hasSapOrder &&
        so.inventory_transfer_status !== "in_transit" &&
        so.inventory_transfer_status !== "received";

    const canReceiveInventory =
        hasPermission("sales_order.write") &&
        so.transaction_type === "inventory_replenishment" &&
        so.inventory_transfer_status === "in_transit";

    // R55: the creator can submit a draft / for-revision / rejected SO for BM approval,
    // mirroring the quotation's "Submit for Approval".
    const canSubmit = hasPermission("sales_order.write") && ["draft", "for_revision", "rejected"].includes(so.status);

    return (
        <div className="fixed inset-0 z-50 bg-black/50 p-3 md:p-6 overflow-y-auto" onClick={onClose}>
            <div className="mx-auto max-w-7xl sc-card bg-background overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="p-4 border-b border-border flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div>
                        <div className="sc-section-title">Sales Order Review</div>
                        <h2 className="text-xl md:text-2xl font-semibold tracking-tight font-mono">{so.doc_no}</h2>
                        <div className="text-sm text-muted-foreground mt-1">
                            {so.customer_name}{(so.end_customer_name || so.customer_snapshot?.company_name) ? ` · ${so.end_customer_name || so.customer_snapshot?.company_name}` : ""}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge status={so.status} />
                        {so.pp_status && (
                            <span className={`sc-pill ${so.pp_status === "picked" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} title="Pick & Pack">
                                {so.pp_status === "picked" ? "Picked" : "Partially picked"}
                                {(so.pp_refs || []).length ? ` · ${so.pp_refs.map((r) => r.doc_no).join(", ")}` : ""}
                            </span>
                        )}
                        {so.payment_status && (() => {
                            const ps = so.payment_status;
                            const cleared = ps === "cleared" || ps === "paid";
                            const forClr = ps === "for_clearing" || ps === "awaiting_treasury";
                            const awaitingTD = forClr && !so.treasury_verified_by;
                            const [lbl, cls] = cleared ? ["CLEARED", "bg-emerald-100 text-emerald-700 border-emerald-200"]
                                : awaitingTD ? ["FOR TREASURY VERIFICATION", "bg-amber-100 text-amber-800 border-amber-200"]
                                : forClr ? ["FOR CLEARING", "bg-amber-100 text-amber-800 border-amber-200"]
                                : ["NOT POSTED", "bg-slate-100 text-slate-600 border-slate-200"];
                            return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${cls}`} title={so.paid_at ? `Cleared ${new Date(so.paid_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}` : "Payment status from Treasury"}>{lbl}</span>;
                        })()}
                        {["treasury", "admin"].includes(user?.role) && ["for_clearing", "awaiting_treasury", "cleared", "paid", "not_posted"].includes(so.payment_status) && (
                            <>
                                <input type="number" step="0.01" min="0" value={verifyAmt} onChange={(e) => setVerifyAmt(e.target.value)} placeholder="Amount verified" title="Amount verified on the deposit slip" className="sc-input h-9 w-36 text-sm" data-testid="verify-amount" />
                                <button onClick={() => onVerifyPayment("not_posted", verifyAmt)} disabled={busy} className="h-9 px-3 rounded border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-60" data-testid="verify-not-posted">Not Posted</button>
                                <button onClick={() => onVerifyPayment("for_clearing", verifyAmt)} disabled={busy} className="h-9 px-3 rounded border border-amber-300 text-amber-800 text-sm font-medium hover:bg-amber-50 disabled:opacity-60">For Clearing</button>
                                <button onClick={() => onVerifyPayment("cleared", verifyAmt)} disabled={busy} className="h-9 px-3 rounded bg-emerald-600 text-white text-sm font-medium hover:opacity-90 disabled:opacity-60">Mark Cleared (Paid)</button>
                            </>
                        )}
                        {!paymentNeedsTreasury(so.payment_type) && !["for_clearing", "cleared", "paid"].includes(so.payment_status) && ["approved", "posted_to_sap", "partially_delivered", "delivered", "invoiced"].includes(so.status) && (hasPermission("sales_order.write") || ["accounting", "admin"].includes(user?.role)) && (
                            <>
                                <input type="number" step="0.01" min="0" value={verifyAmt} onChange={(e) => setVerifyAmt(e.target.value)} placeholder="Amount received" title="Amount received (terms / payment accountability)" className="sc-input h-9 w-32 text-sm" data-testid="record-amount" />
                                <input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="OR / Ref no." title="Official receipt / reference number" className="sc-input h-9 w-28 text-sm" data-testid="record-ref" />
                                <button onClick={() => onRecordPayment({ amount: verifyAmt, reference: payRef })} disabled={busy} className="h-9 px-3 rounded bg-emerald-600 text-white text-sm font-medium hover:opacity-90 disabled:opacity-60" data-testid="record-payment" title="Records the payment and sends it to Treasury (TD) to verify">Record Payment</button>
                            </>
                        )}
                        {so.payment_status === "cleared" && ["approved", "posted_to_sap", "partially_delivered", "delivered", "invoiced"].includes(so.status) && (hasPermission("sales_order.write") || ["accounting", "admin"].includes(user?.role)) && (
                            <button onClick={onCloseSO} disabled={busy} className="h-9 px-3 rounded bg-slate-700 text-white text-sm font-medium hover:opacity-90 disabled:opacity-60" data-testid="close-so" title="Payment is cleared - close the Open SO">Close SO</button>
                        )}
                        <button onClick={onClose} className="h-9 px-3 rounded border border-border text-sm hover:bg-secondary">
                            Close
                        </button>
                    </div>
                </div>

                <div className="p-4 space-y-4">
                    {canSubmit && (
                        <div className="rounded-md border border-blue-300 bg-blue-50 text-blue-900 p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="so-draft-banner">
                            <div className="text-sm flex-1">
                                <span className="font-semibold">Not yet submitted ({so.status === "draft" ? "draft" : so.status.replace(/_/g, " ")}).</span>{" "}
                                Click <b>Submit for Approval</b> to start the approval chain — once approved it posts to SAP automatically.
                            </div>
                            <button disabled={busy} onClick={onSubmit} className="h-9 px-4 inline-flex items-center gap-1 rounded bg-blue-600 text-white text-sm font-medium disabled:opacity-50 shrink-0">
                                <Send className="w-4 h-4" /> Submit for Approval
                            </button>
                        </div>
                    )}
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                        <SummaryCard icon={PackageCheck} label="Ordered Qty" value={num(t.ordered, 0)} />
                        <SummaryCard icon={Truck} label="Delivered Qty" value={num(t.delivered, 0)} />
                        <SummaryCard icon={Boxes} label="Remaining Qty" value={num(t.remaining, 0)} />
                        <SummaryCard icon={Scale} label="Weight" value={`${num(t.weight, 2)} kg`} />
                        <SummaryCard icon={ServerCog} label="SAP Status" value={so.sap_status || "not_posted"} />
                    </div>

                    {Array.isArray(so.sap_documents) && so.sap_documents.length > 0 && (
                        <div className="sc-card p-4">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">SAP Sales Order{so.sap_documents.length > 1 ? "s · split by item group" : ""}</div>
                            <table className="w-full text-sm">
                                <thead><tr className="text-muted-foreground text-left text-xs"><th className="pb-1 pr-4">Item Group</th><th className="pb-1 pr-4">Customer PO</th><th className="pb-1 pr-4">SAP DocNum</th></tr></thead>
                                <tbody>
                                    {so.sap_documents.map((d, i) => (
                                        <tr key={i} className="border-t border-border">
                                            <td className="py-1 pr-4">{d.item_group_name || "All items"}</td>
                                            <td className="py-1 pr-4 font-mono">{d.num_at_card || so.po_no || "—"}</td>
                                            <td className="py-1 pr-4 font-mono">{d.sap_docnum || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {(so.ar_authorized || so.released_at || so.process_remarks || so.release_remarks) && (
                        <div className="sc-card p-4">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Accounting sign-off</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                                <Info label="OK to Process" value={so.ar_authorized_at ? `${so.ar_authorized_by || ""} · ${String(so.ar_authorized_at).slice(0, 10)}` : (so.ar_authorized ? "Yes" : "—")} />
                                <Info label="Process remarks" value={so.process_remarks || "—"} />
                                <Info label="OK to Release" value={so.released_at ? `${so.released_by || ""} · ${String(so.released_at).slice(0, 10)}` : "—"} />
                                <Info label="Release remarks" value={so.release_remarks || "—"} />
                            </div>
                        </div>
                    )}
                    {(so.end_customer_name || so.project_name || so.customer_snapshot?.company_name) && (
                        <div className="sc-card p-4">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Client details (from the quotation)</div>
                            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                                <Info label="End Customer" value={so.end_customer_name || so.customer_snapshot?.company_name || "—"} />
                                <Info label="Contact Person" value={so.contact_person || so.customer_snapshot?.contact_person || "—"} />
                                <Info label="Contact No." value={so.contact_no || so.customer_snapshot?.contact_no || "—"} />
                                <Info label="TIN" value={so.tin || so.customer_snapshot?.tin || "—"} />
                                <Info label="Project Name" value={so.project_name || "—"} />
                                <Info label="Project Address" value={so.project_address || so.customer_snapshot?.address || "—"} />
                                <Info label="Area" value={so.area || "—"} />
                                <Info label="Thru" value={so.thru || "—"} />
                            </div>
                        </div>
                    )}
                    <div className="sc-card p-4 grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-3 text-sm">
                        <Info label="Source Quotation" value={so.source_quotation_no || "—"} mono />
                        <Info label="PO No." value={so.po_no || "—"} mono />
                        <Info label="Payment Type" value={so.payment_type || "—"} />
                        <Info label="Subtotal" value={showPrices ? `₱${money(so.subtotal)}` : "—"} />
                        <Info label="Other Charges" value={showPrices ? `₱${money(so.other_charges_total || 0)}` : "—"} />
                        <Info label="Grand Total" value={showPrices ? `₱${money(so.grand_total ?? so.subtotal)}` : "—"} />
                        <Info label="Payment Verification" value={so.payment_verification || (paymentNeedsTreasury(so.payment_type) ? "For verification to Treasury" : "—")} />
                        <Info label="Approval Step" value={so.approval_step || pendingApproval?.approval_step || "—"} />
                        <Info
                            label="Transaction Type"
                            value={so.transaction_type === "inventory_replenishment" ? "Inventory Replenishment" : "Customer Sale"}
                        />
                        <Info label="Business Mode" value={so.business_mode || "organic"} />
                        <Info label="Company" value={so.company || "—"} />
                        <Info label="Transfer Status" value={so.inventory_transfer_status || "—"} />
                        <Info label="Import Dept" value={so.sent_to_import ? "PO sent to Import" : "—"} />
                    </div>

                    <CsrCard
                        so={so}
                        editable={so.status !== "cancelled" && hasPermission("sales_order.write")}
                        onUpdated={onUpdated}
                    />

                    <div className="space-y-4">
                        <div>
                            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Payment attachments</div>
                            <Attachments collection="sales-orders" docId={so.id} editable={hasPermission("sales_order.write") || ["accounting", "treasury", "admin"].includes(user?.role)} kinds={SO_PAYMENT_UPLOAD} displayKinds={SO_PAYMENT_DISPLAY} onlyKinds={SO_PAYMENT_ONLY} defaultKind="card" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Document attachments</div>
                            <Attachments collection="sales-orders" docId={so.id} editable={hasPermission("sales_order.write") || ["accounting", "treasury", "admin"].includes(user?.role)} kinds={SO_DOC_UPLOAD} displayKinds={SO_DOC_DISPLAY} onlyKinds={SO_DOC_ONLY} defaultKind="other" />
                        </div>
                    </div>

                    {so.transaction_type === "inventory_replenishment" && (
                        <div className="sc-card p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm bg-secondary/30">
                            <Info label="Source Schema" value={so.source_schema_name || "—"} mono />
                            <Info label="Destination Schema" value={so.destination_schema_name || "—"} mono />
                            <Info label="Destination Warehouse" value={so.destination_warehouse_code || "—"} mono />
                        </div>
                    )}

                    <div className="sc-card p-4 space-y-3">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div>
                                <div className="sc-section-title">Available Actions</div>
                                <div className="text-xs text-muted-foreground">
                                    Actions are role-aware and still enforced by backend permissions.
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {canApprove && (
                                    <>
                                        <button disabled={busy} onClick={onApprove} className="h-9 px-3 inline-flex items-center gap-1 rounded bg-emerald-600 text-white text-sm disabled:opacity-50">
                                            <Check className="w-4 h-4" /> Approve
                                        </button>
                                        <button disabled={busy} onClick={onRevise} className="h-9 px-3 inline-flex items-center gap-1 rounded border border-border text-sm hover:bg-secondary disabled:opacity-50">
                                            <Undo2 className="w-4 h-4" /> Revise
                                        </button>
                                        <button disabled={busy} onClick={onReject} className="h-9 px-3 inline-flex items-center gap-1 rounded bg-red-600 text-white text-sm disabled:opacity-50">
                                            <X className="w-4 h-4" /> Reject
                                        </button>
                                    </>
                                )}

                                {canPostSap && (
                                    <button disabled={busy} onClick={onPostToSap} className="h-9 px-3 inline-flex items-center gap-1 rounded border border-border text-sm hover:bg-secondary disabled:opacity-50">
                                        <ServerCog className="w-4 h-4" /> Post to SAP
                                    </button>
                                )}

                                {canPicklist && (
                                    <button disabled={busy} onClick={onCreatePicklist} className="h-9 px-3 inline-flex items-center gap-1 rounded border border-border text-sm hover:bg-secondary disabled:opacity-50">
                                        <ClipboardList className="w-4 h-4" /> Create Picklist
                                    </button>
                                )}

                                {canMarkInTransit && (
                                    <button disabled={busy} onClick={onMarkInTransit} className="h-9 px-3 inline-flex items-center gap-1 rounded border border-border text-sm hover:bg-secondary disabled:opacity-50">
                                        <Truck className="w-4 h-4" /> Mark In Transit
                                    </button>
                                )}

                                {canReceiveInventory && (
                                    <button disabled={busy} onClick={onReceiveInventory} className="h-9 px-3 inline-flex items-center gap-1 rounded bg-emerald-600 text-white text-sm disabled:opacity-50">
                                        <Boxes className="w-4 h-4" /> Receive Inventory
                                    </button>
                                )}

                                {canSubmit && (
                                    <button disabled={busy} onClick={onSubmit} className="h-9 px-3 inline-flex items-center gap-1 rounded bg-blue-600 text-white text-sm disabled:opacity-50">
                                        <Send className="w-4 h-4" /> Submit for Approval
                                    </button>
                                )}

                                <button onClick={() => window.open(`/sales-orders/${so.id}/print`, "_blank")} className="h-9 px-3 inline-flex items-center gap-1 rounded border border-border text-sm hover:bg-secondary">
                                    <Printer className="w-4 h-4" /> Print
                                </button>

                                {!canApprove && !canPostSap && !canPicklist && !canMarkInTransit && !canReceiveInventory && !canSubmit && (
                                    <div className="text-xs text-muted-foreground border border-border rounded px-3 py-2">
                                        No workflow action for your role or current SO status.
                                    </div>
                                )}
                            </div>
                        </div>

                        {canApprove && (
                            <input
                                className="sc-input"
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Optional approval / rejection / revision comment..."
                            />
                        )}
                    </div>

                    <div className="sc-card overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full sc-table min-w-[1100px]">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Schema / WH</th>
                                        <th className="text-right">Qty</th>
                                        <th className="text-right">Boxes</th>
                                        <th className="text-right">Pcs</th>
                                        <th className="text-right">Weight</th>
                                        <th className="text-right">Gross Price</th>
                                        <th className="text-right">Disc Price</th>
                                        <th className="text-right">Line Total</th>
                                        <th>Tonality</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(so.lines || []).map((l) => (
                                        <tr key={l.id || l.line_id}>
                                            <td>
                                                <div className="font-mono text-xs">{l.item_code}</div>
                                                <div className="text-xs text-muted-foreground">{l.item_name}</div>
                                                <div className="text-[11px] text-muted-foreground">{l.size || "—"}</div>
                                            </td>
                                            <td>
                                                <div className="font-mono text-xs">{l.schema_name || "—"}</div>
                                                <div className="font-mono text-xs text-muted-foreground">{l.warehouse_code || "—"}</div>
                                            </td>
                                            <td className="sc-num">{num(l.quantity, 0)}</td>
                                            <td className="sc-num">{num(l.estimated_boxes, 2)}</td>
                                            <td className="sc-num">{Number(l.loose_pcs ?? 0)}</td>
                                            <td className="sc-num">{num(l.total_weight, 2)} {l.weight_unit || "kg"}</td>
                                            <td className="sc-num">{showPrices ? `₱${money(l.given_price ?? l.markup_price ?? l.dp_price ?? l.unit_price)}` : "—"}</td>
                                            <td className="sc-num">{showPrices ? `₱${money(Number(l.quantity) > 0 ? Number(l.net_total ?? l.line_total ?? (Number(l.unit_price || 0) * Number(l.quantity || 0))) / Number(l.quantity) : (l.discounted_price ?? l.unit_price))}` : "—"}</td>
                                            <td className="sc-num font-semibold">{showPrices ? `₱${money(l.net_total ?? l.line_total ?? (Number(l.unit_price || 0) * Number(l.quantity || 0)))}` : "—"}</td>
                                            <td className="text-[11px] text-emerald-700 whitespace-nowrap">{(l.allocation?.allocated_batches?.length
                                                ? l.allocation.allocated_batches.map((b) => `${b.batch_code || b.tonality}×${Number(b.quantity || 0)}`).join(", ")
                                                : (l.batch_code || l.requested_tonality || "—"))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <HistoryCard title="Status History" rows={so.status_history || []} />
                        <ApprovalHistoryCard rows={so.approval_history || []} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function Info({ label, value, mono }) {
    return (
        <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
            <div className={`mt-1 ${mono ? "font-mono text-xs" : "font-medium"}`}>{value}</div>
        </div>
    );
}

function HistoryCard({ title, rows }) {
    return (
        <div className="sc-card p-4">
            <div className="sc-section-title mb-2">{title}</div>
            {rows.length === 0 ? (
                <div className="text-sm text-muted-foreground">No history yet.</div>
            ) : (
                <ul className="space-y-2">
                    {rows.map((h, i) => (
                        <li key={i} className="text-sm flex flex-wrap items-center gap-2">
                            <span className="text-xs text-muted-foreground font-mono">
                                {h.at ? new Date(h.at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"}
                            </span>
                            <StatusBadge status={h.status || h.action || "updated"} />
                            <span className="text-muted-foreground">by {h.by || "—"}</span>
                            {h.comment && <span className="text-muted-foreground italic">— {h.comment}</span>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function ApprovalHistoryCard({ rows }) {
    return (
        <div className="sc-card p-4">
            <div className="sc-section-title mb-2">Approval History</div>
            {rows.length === 0 ? (
                <div className="text-sm text-muted-foreground">No approval history yet.</div>
            ) : (
                <ul className="space-y-2">
                    {rows.map((h, i) => (
                        <li key={i} className="text-sm flex flex-wrap items-center gap-2">
                            <span className="text-xs text-muted-foreground font-mono">
                                {h.at ? new Date(h.at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"}
                            </span>
                            <StatusBadge status={h.action || "approval"} />
                            <span className="text-muted-foreground">step {h.step || "—"}</span>
                            <span className="text-muted-foreground">by {h.by || "—"}</span>
                            {h.comment && <span className="text-muted-foreground italic">— {h.comment}</span>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function SummaryCard({ icon: Icon, label, value }) {
    return (
        <div className="sc-card p-4">
            <div className="flex items-center justify-between gap-2">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {label}
                    </div>
                    <div className="text-lg font-semibold mt-1">{value}</div>
                </div>

                {Icon && <Icon className="w-5 h-5 text-muted-foreground" />}
            </div>
        </div>
    );
}

export function SalesOrderDetail() {
    const { id } = useParams();
    const [so, setSo] = useState(null);

    useEffect(() => {
        (async () => {
            const { data } = await api.get(`/sales-orders/${id}`);
            setSo(data);
        })();
    }, [id]);

    if (!so) return <div className="p-6">Loading…</div>;

    return (
        <div className="p-3 md:p-6">
            <SalesOrderModal
                so={so}
                user={{ role: "admin" }}
                hasPermission={() => true}
                busy={false}
                comment=""
                setComment={() => {}}
                onClose={() => window.history.back()}
                onApprove={() => {}}
                onRevise={() => {}}
                onReject={() => {}}
                onPostToSap={() => {}}
                onCreatePicklist={() => {}}
                onSubmit={() => {}}
                pendingApproval={null}
                onUpdated={setSo}
            />
        </div>
    );
}
